/**
 * Locale-aware terminology for the Open platform.
 *
 * Maps UK English (en-GB) and US English (en-US) academic terms.
 * Add new entries here when user-facing text needs to vary by locale.
 */

export type AppLocale = "en-GB" | "en-US";

export interface Terms {
  /** Singular: "Module" (GB) / "Course" (US) */
  module: string;
  /** Plural: "Modules" (GB) / "Courses" (US) */
  modules: string;
  /** Lowercase singular */
  module_lc: string;
  /** Lowercase plural */
  modules_lc: string;
  /** "degree" (GB) / "studies" (US) */
  degree: string;
  /** "university" (GB) / "school" (US) */
  university: string;
  /** "university degree" (GB) / "college experience" (US) */
  universityDegree: string;
  /** "university students" (GB) / "students" (US) */
  universityStudents: string;
  /** "university student" (GB) / "college student" (US) */
  universityStudent: string;
  /** "past papers" (GB) / "practice exams" (US) */
  pastPapers: string;
  /** "organised" (GB) / "organized" (US) */
  organised: string;
  /** "organises" (GB) / "organizes" (US) */
  organises: string;
  /** "colour" (GB) / "color" (US) */
  colour: string;
}

const EN_GB: Terms = {
  module: "Module",
  modules: "Modules",
  module_lc: "module",
  modules_lc: "modules",
  degree: "degree",
  university: "university",
  universityDegree: "university degree",
  universityStudents: "university students",
  universityStudent: "university student",
  pastPapers: "past papers",
  organised: "organised",
  organises: "organises",
  colour: "colour",
};

const EN_US: Terms = {
  module: "Course",
  modules: "Courses",
  module_lc: "course",
  modules_lc: "courses",
  degree: "studies",
  university: "school",
  universityDegree: "college experience",
  universityStudents: "students",
  universityStudent: "college student",
  pastPapers: "practice exams",
  organised: "organized",
  organises: "organizes",
  colour: "color",
};

const TERMS_BY_LOCALE: Record<AppLocale, Terms> = {
  "en-GB": EN_GB,
  "en-US": EN_US,
};

/** Default locale when detection fails or is ambiguous. */
export const DEFAULT_LOCALE: AppLocale = "en-US";

export function getTerms(locale: AppLocale): Terms {
  return TERMS_BY_LOCALE[locale];
}
